import { type FC } from "react";

import { Button } from "@styled/button";
import { ErrorLine } from "@styled/error_line";
import { LinkButton } from "@styled/link_button";
import { NoticeLine } from "@styled/notice_line";
import { SectionsHeader, SectionsPanel } from "@styled/sections_header";
import {
  SetupReviewActions,
  SetupReviewCard,
  SetupReviewGroup,
  SetupReviewNote,
  SetupReviewSkeleton,
} from "@styled/setup_review";

import { DraftingFieldRow } from "./drafting_field_row";
import { DraftingSetupChanges } from "./drafting_setup_changes";
import { DraftingSetupRow } from "./drafting_setup_row";
import { useDraftingSectionsOf } from "./use_drafting_sections";
import { type useDraftingSetup } from "./use_drafting_setup";

type Setup = ReturnType<typeof useDraftingSetup>;
type Proposal = NonNullable<Setup["proposal"]>;

// The review's rows still loading, under `line`.
const SetupLoading: FC<{ line: string }> = ({ line }) => (
  <SetupReviewCard title={line} lead="" footer={null}>
    <SetupReviewSkeleton />
  </SetupReviewCard>
);

// While his Word file is read, before Your sections can show: the review's
// loading card, so it carries on into finding its sections in one piece.
export const DraftingSetupReading: FC = () => (
  <SectionsPanel>
    <SectionsHeader line="" action={null} />
    <SetupLoading line="Reading your Word file…" />
  </SectionsPanel>
);

// The review's one action: Done when nothing changed, else what it does.
const addLabel = (
  proposal: Pick<Proposal, "changed" | "unchanged">,
  kept: number,
) => {
  if (proposal.unchanged) return "Done";
  if (proposal.changed) return "Update sections";
  return `Add ${String(kept)} ${kept === 1 ? "section" : "sections"}`;
};

// Easy setup's review in place of an empty Your sections: each section it
// found in his template, to keep, rename or point at other sources, then
// "Add N sections" at once, or Skip to add them himself.
export const DraftingSetupReview: FC = () => {
  const { fields, setup } = useDraftingSectionsOf();
  const { proposal } = setup;
  if (setup.finding) return <SetupLoading line="Finding its sections…" />;
  if (!proposal) return null;
  // Once his template changed, his sections from before go in as he set them,
  // unshown; only the new ones are his to look over.
  const shown = proposal.sections.filter((section) => !section.kept);
  const fieldNotes = proposal.removedFields.map((name) => (
    <SetupReviewNote key={name}>
      {name} · no longer in your template
    </SetupReviewNote>
  ));

  return (
    <SetupReviewCard
      title={proposal.title}
      lead={proposal.lead}
      footer={
        <>
          <SetupReviewActions
            add={
              <Button
                type="button"
                color="navy"
                loading={setup.applying}
                disabled={setup.keptCount === 0}
                onClick={() => void setup.apply()}
              >
                {addLabel(proposal, setup.keptCount)}
              </Button>
            }
            skipLabel={
              proposal.changed ? "Not now" : "Skip, I'll add them myself"
            }
            onSkip={proposal.unchanged ? undefined : setup.skip}
          />
          {setup.error && (
            <ErrorLine>
              {setup.error}{" "}
              <LinkButton onClick={setup.reload}>Reload</LinkButton>
            </ErrorLine>
          )}
          <NoticeLine>
            You can rename, change what a section reads, or add more any time in
            Your sections.
          </NoticeLine>
        </>
      }
    >
      {(proposal.fields.length > 0 || fieldNotes.length > 0) && (
        <SetupReviewGroup
          label={proposal.changed ? "Fields" : "Header fields"}
          line={proposal.fieldsLine}
        >
          {proposal.fields.map((field) => (
            <DraftingFieldRow
              key={field.kind}
              field={field}
              onSave={fields.update}
            />
          ))}
          {fieldNotes}
        </SetupReviewGroup>
      )}
      {fields.error && <ErrorLine>{fields.error}</ErrorLine>}
      {shown.length > 0 && (
        <SetupReviewGroup
          label={proposal.changed ? "New sections" : "Sections"}
          line="in your template's order"
        >
          {shown.map((section) => (
            <DraftingSetupRow
              key={section.block}
              section={section}
              setup={setup}
            />
          ))}
        </SetupReviewGroup>
      )}
      <DraftingSetupChanges
        removed={proposal.removed}
        renamed={proposal.renamed}
      />
    </SetupReviewCard>
  );
};
